import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { ThemeContext, type ThemeContextValue } from '@/contexts/ThemeContext';
import { useSettings } from '@/hooks/useSettings';
import type { ResolvedTheme, ThemePreference } from '@/types';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function resolve(preference: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const { settings, update } = useSettings();
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, []);

  const resolved = resolve(settings.theme, systemPrefersDark);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
  }, [resolved]);

  const setPreference = useCallback(
    (preference: ThemePreference) => {
      void update({ theme: preference });
    },
    [update],
  );

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference: settings.theme,
      resolved,
      setPreference,
      toggle: () => setPreference(resolved === 'dark' ? 'light' : 'dark'),
    }),
    [settings.theme, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
